// Sales Coach Réel — rendu des sections spécifiques par contextType (PR3, 2026-05-19).
// Consomme debrief.contextSpecific produit par analyzeContextSpecific (PR2) et
// affiche le variant correspondant : cold_call, discovery, pitch, négo, CS, qualif, generic.
//
// Mocks-up validés Fabien 2026-05-19. Pas de référence à la méthode Échiquier.
// Mot "besoin" : affichage informatif et bienveillant via besoinUsageNote.
//
// Tous les sous-champs sont optionnels côté type — le composant fait un rendu
// défensif et n'affiche que les blocs effectivement présents.

import { useTranslation } from "react-i18next";
import { Card } from "./ui/card";
import { CheckCircle2, XCircle, AlertCircle, ArrowRight, Mic, Search, Presentation, Handshake, HeartHandshake, Target as TargetIcon, Sparkles } from "lucide-react";

type ContextSpecificPart =
  | { type: "cold_call"; firstThreeSeconds?: string; objectionsHandled?: Array<{ objection: string; handled: "well" | "partial" | "abandoned"; citation?: string }>; engagement?: { status: "dated_meeting" | "undated_meeting" | "refusal" | "vague_followup"; citation?: string; confirmationRequested?: boolean } }
  | { type: "discovery"; spinCoverage?: { situation: CoverageStatus; problem: CoverageStatus; implication: CoverageStatus; needPayoff: CoverageStatus; evidence?: string }; bantCoverage?: { budget: CoverageStatus; authority: CoverageStatus; need: CoverageStatus; timing: CoverageStatus; evidence?: string }; icebergLevers?: Array<"prise_conscience" | "envie" | "frustration" | "impatience">; maturityLevel?: "prise_conscience" | "consideration" | "achat" | "usage"; besoinUsageNote?: { occurrences: number; citations: string[]; message?: string } }
  | { type: "pitch"; interlocuteurs?: Array<{ name?: string; role?: string; participation: "high" | "medium" | "silent"; interventionsCount?: number }>; softObjections?: Array<{ objection: string; handling: "well" | "partial" | "missed"; citation?: string }>; adhesionByInterlocuteur?: Array<{ name?: string; role?: string; adhesion: "explicit" | "implicit" | "no_signal" | "not_solicited" }> }
  | { type: "negotiation"; negoMovements?: Array<{ objection: string; response: string; counterpartRequested: boolean; paramMoved: "unit_price" | "perimeter" | "duration" | "users" | "service_level" | "none"; citation?: string }>; antiPatterns?: { coupePoireEnDeux: boolean; questionBudgetAsked: boolean; discountWithoutCounterpart: boolean; competitorChallengedOn4Params: boolean }; estimatedMarginImpact?: string }
  | { type: "customer_success"; expansionSignals?: Array<{ citation: string; interpretation: string }>; churnSignals?: Array<{ citation: string; interpretation: string; severity: "high" | "medium" | "low"; handled: boolean }>; ambassadorActivation?: { referenceRequested: boolean; testimonialAgreed: boolean; crossSellWorkshopProposed: boolean } }
  | { type: "qualification"; meddicTable?: Array<{ dimension: "metrics" | "economic_buyer" | "decision_criteria" | "decision_process" | "implicit_pain" | "champion"; status: CoverageStatus; evidence?: string }>; championAnalysis?: { identified: boolean; activated: boolean; name?: string; gap?: string }; decisionProcessTraced?: { milestones: string[]; dates: string[]; people: string[] } }
  | { type: "generic"; suggestion?: string };

type CoverageStatus = "covered" | "partial" | "missing";

export function ContextSpecificSection({ contextSpecific }: { contextSpecific: ContextSpecificPart | null | undefined }) {
  if (!contextSpecific || !contextSpecific.type) return null;

  switch (contextSpecific.type) {
    case "cold_call":    return <ColdCallSection data={contextSpecific} />;
    case "discovery":    return <DiscoverySection data={contextSpecific} />;
    case "pitch":        return <PitchSection data={contextSpecific} />;
    case "negotiation":  return <NegotiationSection data={contextSpecific} />;
    case "customer_success": return <CustomerSuccessSection data={contextSpecific} />;
    case "qualification": return <QualificationSection data={contextSpecific} />;
    case "generic":      return <GenericSection data={contextSpecific} />;
    default:             return null;
  }
}

// ============================================================
// Wrapper visuel commun (un peu de répétition acceptée pour la lisibilité)
// ============================================================
function Frame({ title, icon, iconColor, children }: { title: string; icon: React.ReactNode; iconColor: string; children: React.ReactNode }) {
  return (
    <Card className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: iconColor }}>
          <span className="text-white">{icon}</span>
        </div>
        <h2 className="text-base md:text-lg font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          {title}
        </h2>
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

// Les statuts sont des CODES (`covered` | `partial` | `missing`) : le libellé est
// résolu à l'affichage via t(), jamais stocké en dur — sinon la version anglaise
// de l'écran affiche « COUVERT ».
const COVERAGE_COLORS: Record<CoverageStatus, { labelKey: string; bg: string; text: string; icon: React.ReactNode }> = {
  covered: { labelKey: "salesCoach.contextSpecific.coverage.covered", bg: "#F0FDF4", text: "#1E7A3C", icon: <CheckCircle2 className="h-4 w-4" /> },
  partial: { labelKey: "salesCoach.contextSpecific.coverage.partial", bg: "#FFFBEB", text: "#926B00", icon: <AlertCircle className="h-4 w-4" /> },
  missing: { labelKey: "salesCoach.contextSpecific.coverage.missing", bg: "#FFF5F5", text: "#C0423F", icon: <XCircle className="h-4 w-4" /> },
};

function CoverageBadge({ status, label }: { status: CoverageStatus; label: string }) {
  const { t } = useTranslation();
  const cfg = COVERAGE_COLORS[status];
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: cfg.bg, color: cfg.text }}>
      {cfg.icon}
      <span className="font-medium">{label}</span>
      <span className="ml-auto text-xs uppercase">{t(cfg.labelKey)}</span>
    </div>
  );
}

// ============================================================
// 1. COLD CALL
// ============================================================
function ColdCallSection({ data }: { data: Extract<ContextSpecificPart, { type: "cold_call" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.frameColdCall")} icon={<Mic className="h-5 w-5" />} iconColor="#384872">
      {data.firstThreeSeconds && (
        <div>
          <h3 className="text-sm font-semibold mb-1" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.firstThreeSeconds")}</h3>
          <p className="text-sm italic" style={{ color: "#384872" }}>« {data.firstThreeSeconds} »</p>
        </div>
      )}

      {data.objectionsHandled && data.objectionsHandled.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.resistancesEncountered")}</h3>
          <div className="space-y-2">
            {data.objectionsHandled.map((o, i) => (
              <div key={i} className="p-3 rounded-lg bg-gray-50 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium" style={{ color: "#384872" }}>« {o.objection} »</span>
                  <HandledBadge value={o.handled} />
                </div>
                {o.citation && <p className="text-xs italic text-gray-600">« {o.citation} »</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.engagement && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.finalEngagement")}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <EngagementBadge status={data.engagement.status} />
            {data.engagement.confirmationRequested != null && (
              <span className="text-xs text-gray-600">
                {t("salesCoach.contextSpecific.confirmationRequested")}{" "}
                {data.engagement.confirmationRequested ? t("salesCoach.contextSpecific.yes") : t("salesCoach.contextSpecific.no")}
              </span>
            )}
          </div>
          {data.engagement.citation && (
            <p className="text-xs italic text-gray-600 mt-2">« {data.engagement.citation} »</p>
          )}
        </div>
      )}
    </Frame>
  );
}

function HandledBadge({ value }: { value: "well" | "partial" | "abandoned" }) {
  const { t } = useTranslation();
  const cfg = {
    well: { key: "well", bg: "#F0FDF4", text: "#1E7A3C" },
    partial: { key: "partial", bg: "#FFFBEB", text: "#926B00" },
    abandoned: { key: "abandoned", bg: "#FFF5F5", text: "#C0423F" },
  }[value];
  return <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.text }}>{t(`salesCoach.contextSpecific.handled.${cfg.key}`)}</span>;
}

function EngagementBadge({ status }: { status: "dated_meeting" | "undated_meeting" | "refusal" | "vague_followup" }) {
  const { t } = useTranslation();
  const cfg = {
    dated_meeting: { bg: "#F0FDF4", text: "#1E7A3C" },
    undated_meeting: { bg: "#FFFBEB", text: "#926B00" },
    refusal: { bg: "#F0F7FF", text: "#384872" },
    vague_followup: { bg: "#FFF5F5", text: "#C0423F" },
  }[status];
  return <span className="text-sm px-3 py-1 rounded-full font-medium" style={{ backgroundColor: cfg.bg, color: cfg.text }}>{t(`salesCoach.contextSpecific.engagement.${status}`)}</span>;
}

// ============================================================
// 2. DISCOVERY
// ============================================================
function DiscoverySection({ data }: { data: Extract<ContextSpecificPart, { type: "discovery" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.frameDiscovery")} icon={<Search className="h-5 w-5" />} iconColor="#5A4BBE">
      {data.spinCoverage && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.spinCoverage")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <CoverageBadge status={data.spinCoverage.situation} label="Situation" />
            <CoverageBadge status={data.spinCoverage.problem} label="Problem" />
            <CoverageBadge status={data.spinCoverage.implication} label="Implication" />
            <CoverageBadge status={data.spinCoverage.needPayoff} label="Need-payoff" />
          </div>
          {data.spinCoverage.evidence && (
            <p className="text-xs italic text-gray-600 mt-2">« {data.spinCoverage.evidence} »</p>
          )}
        </div>
      )}

      {data.bantCoverage && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.bantCoverage")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <CoverageBadge status={data.bantCoverage.budget} label="Budget" />
            <CoverageBadge status={data.bantCoverage.authority} label="Authority" />
            <CoverageBadge status={data.bantCoverage.need} label="Need" />
            <CoverageBadge status={data.bantCoverage.timing} label="Timing" />
          </div>
          {data.bantCoverage.evidence && (
            <p className="text-xs italic text-gray-600 mt-2">« {data.bantCoverage.evidence} »</p>
          )}
        </div>
      )}

      {data.icebergLevers && data.icebergLevers.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.icebergLevers")}</h3>
          <div className="flex flex-wrap gap-2">
            {(["prise_conscience", "envie", "frustration", "impatience"] as const).map((lever) => {
              const active = data.icebergLevers!.includes(lever);
              return (
                <span
                  key={lever}
                  className="text-sm px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: active ? "#F0FDF4" : "#F3F4F6",
                    color: active ? "#1E7A3C" : "#9CA3AF",
                    textDecoration: active ? "none" : "line-through",
                  }}
                >
                  {active ? "✅ " : "○ "}{t(`salesCoach.contextSpecific.iceberg.${lever}`)}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {data.maturityLevel && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.maturityDetected")}</h3>
          <span className="text-sm px-3 py-1 rounded-full font-medium" style={{ backgroundColor: "#F0F7FF", color: "#384872" }}>
            {t(`salesCoach.contextSpecific.maturity.${data.maturityLevel}`)}
          </span>
        </div>
      )}

      {data.besoinUsageNote && data.besoinUsageNote.occurrences > 0 && (
        <div className="p-3 rounded-lg" style={{ backgroundColor: "#F0F7FF", borderLeft: "3px solid #A6D3F9" }}>
          <h3 className="text-sm font-semibold mb-1" style={{ color: "#384872" }}>
            {t("salesCoach.contextSpecific.lexicalNote")}
          </h3>
          <p className="text-xs text-gray-700">
            {t("salesCoach.contextSpecific.lexicalNoteBody", { count: data.besoinUsageNote.occurrences })}
          </p>
          {data.besoinUsageNote.citations.length > 0 && (
            <ul className="mt-2 space-y-1">
              {data.besoinUsageNote.citations.slice(0, 3).map((c, i) => (
                <li key={i} className="text-xs italic text-gray-600">« {c} »</li>
              ))}
            </ul>
          )}
          {data.besoinUsageNote.message && (
            <p className="text-xs text-gray-700 mt-2">{data.besoinUsageNote.message}</p>
          )}
        </div>
      )}
    </Frame>
  );
}

// ============================================================
// 3. PITCH / DÉMO / PROPOSITION
// ============================================================
function PitchSection({ data }: { data: Extract<ContextSpecificPart, { type: "pitch" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.framePitch")} icon={<Presentation className="h-5 w-5" />} iconColor="#926B00">
      {data.interlocuteurs && data.interlocuteurs.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.interlocutorsDetected")}</h3>
          <div className="space-y-2">
            {data.interlocuteurs.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 text-sm">
                <div>
                  <span className="font-medium" style={{ color: "#384872" }}>{p.name ?? t("salesCoach.contextSpecific.interlocutorFallback")}</span>
                  {p.role && <span className="text-gray-600 ml-2">— {p.role}</span>}
                  {p.interventionsCount != null && (
                    <span className="text-xs text-gray-500 ml-2">{t("salesCoach.contextSpecific.interventionsCount", { count: p.interventionsCount })}</span>
                  )}
                </div>
                <ParticipationBadge value={p.participation} />
              </div>
            ))}
          </div>
        </div>
      )}

      {data.softObjections && data.softObjections.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.softObjections")}</h3>
          <div className="space-y-2">
            {data.softObjections.map((o, i) => (
              <div key={i} className="p-3 rounded-lg bg-gray-50 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium" style={{ color: "#384872" }}>« {o.objection} »</span>
                  <SoftObjBadge value={o.handling} />
                </div>
                {o.citation && <p className="text-xs italic text-gray-600">« {o.citation} »</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.adhesionByInterlocuteur && data.adhesionByInterlocuteur.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.adhesionByInterlocutor")}</h3>
          <div className="space-y-1">
            {data.adhesionByInterlocuteur.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span style={{ color: "#384872" }}>{a.name ?? "—"}{a.role && ` (${a.role})`}</span>
                <AdhesionBadge value={a.adhesion} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Frame>
  );
}

function ParticipationBadge({ value }: { value: "high" | "medium" | "silent" }) {
  const { t } = useTranslation();
  const cfg = {
    high: { bg: "#F0FDF4", text: "#1E7A3C" },
    medium: { bg: "#FFFBEB", text: "#926B00" },
    silent: { bg: "#FFF5F5", text: "#C0423F" },
  }[value];
  return <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.text }}>{t(`salesCoach.contextSpecific.participation.${value}`)}</span>;
}

function SoftObjBadge({ value }: { value: "well" | "partial" | "missed" }) {
  const { t } = useTranslation();
  const cfg = {
    well: { bg: "#F0FDF4", text: "#1E7A3C" },
    partial: { bg: "#FFFBEB", text: "#926B00" },
    missed: { bg: "#FFF5F5", text: "#C0423F" },
  }[value];
  return <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.text }}>{t(`salesCoach.contextSpecific.softObjHandling.${value}`)}</span>;
}

function AdhesionBadge({ value }: { value: "explicit" | "implicit" | "no_signal" | "not_solicited" }) {
  const { t } = useTranslation();
  const cfg = {
    explicit: { bg: "#F0FDF4", text: "#1E7A3C" },
    implicit: { bg: "#FFFBEB", text: "#926B00" },
    no_signal: { bg: "#F3F4F6", text: "#6B7280" },
    not_solicited: { bg: "#FFF5F5", text: "#C0423F" },
  }[value];
  return <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: cfg.bg, color: cfg.text }}>{t(`salesCoach.contextSpecific.adhesion.${value}`)}</span>;
}

// ============================================================
// 4. NÉGOCIATION
// ============================================================
// Le paramètre bougé est un CODE : seule la couleur est portée ici, le libellé
// est résolu via t() (`paramMovedLabels.<code>`).
const PARAM_MOVED_COLORS: Record<string, string> = {
  unit_price: "#C0423F",
  perimeter: "#1E7A3C",
  duration: "#1E7A3C",
  users: "#1E7A3C",
  service_level: "#1E7A3C",
  none: "#6B7280",
};

function NegotiationSection({ data }: { data: Extract<ContextSpecificPart, { type: "negotiation" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.frameNegotiation")} icon={<Handshake className="h-5 w-5" />} iconColor="#5A4BBE">
      {data.negoMovements && data.negoMovements.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.negoMovementsLog")}</h3>
          <div className="space-y-3">
            {data.negoMovements.map((m, i) => {
              const paramColor = PARAM_MOVED_COLORS[m.paramMoved] ?? PARAM_MOVED_COLORS.none;
              return (
                <div key={i} className="p-3 rounded-lg bg-gray-50 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-2 items-center mb-2">
                    <span className="text-gray-700">« {m.objection} »</span>
                    <ArrowRight className="h-4 w-4 text-gray-400 mx-auto" />
                    <span style={{ color: "#384872" }}>{m.response}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap text-xs">
                    <span className="text-gray-500">
                      {t("salesCoach.contextSpecific.counterpart")}{" "}
                      {m.counterpartRequested
                        ? t("salesCoach.contextSpecific.counterpartRequested")
                        : t("salesCoach.contextSpecific.counterpartNotRequested")}
                    </span>
                    <span style={{ color: paramColor }}>
                      {t("salesCoach.contextSpecific.paramMoved")}{" "}
                      {t(`salesCoach.contextSpecific.paramMovedLabels.${m.paramMoved}`, {
                        defaultValue: t("salesCoach.contextSpecific.paramMovedLabels.none"),
                      })}
                    </span>
                  </div>
                  {m.citation && <p className="text-xs italic text-gray-600 mt-1">« {m.citation} »</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.antiPatterns && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.antiPatternsDetected")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <AntiPattern label={t("salesCoach.contextSpecific.antiPatterns.splitTheDifference")} value={data.antiPatterns.coupePoireEnDeux} />
            <AntiPattern label={t("salesCoach.contextSpecific.antiPatterns.budgetQuestion")} value={data.antiPatterns.questionBudgetAsked} />
            <AntiPattern label={t("salesCoach.contextSpecific.antiPatterns.discountWithoutCounterpart")} value={data.antiPatterns.discountWithoutCounterpart} />
            <AntiPattern label={t("salesCoach.contextSpecific.antiPatterns.competitorChallenged")} value={data.antiPatterns.competitorChallengedOn4Params} positive />
          </div>
        </div>
      )}

      {data.estimatedMarginImpact && (
        <div className="p-3 rounded-lg" style={{ backgroundColor: "#FFFBEB", borderLeft: "3px solid #F6C847" }}>
          <h3 className="text-sm font-semibold mb-1" style={{ color: "#926B00" }}>{t("salesCoach.contextSpecific.estimatedMarginImpact")}</h3>
          <p className="text-sm text-gray-800">{data.estimatedMarginImpact}</p>
        </div>
      )}
    </Frame>
  );
}

function AntiPattern({ label, value, positive = false }: { label: string; value: boolean; positive?: boolean }) {
  // Pour la plupart : true = anti-pattern détecté = rouge.
  // Pour "competitorChallengedOn4Params" : true = bonne pratique = vert.
  const isBad = positive ? !value : value;
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg text-sm" style={{ backgroundColor: isBad ? "#FFF5F5" : "#F0FDF4", color: isBad ? "#C0423F" : "#1E7A3C" }}>
      {isBad ? <XCircle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
      <span>{label}</span>
    </div>
  );
}

// ============================================================
// 5. CUSTOMER SUCCESS
// ============================================================
function CustomerSuccessSection({ data }: { data: Extract<ContextSpecificPart, { type: "customer_success" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.frameCustomerSuccess")} icon={<HeartHandshake className="h-5 w-5" />} iconColor="#1E7A3C">
      {data.expansionSignals && data.expansionSignals.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.expansionSignals")}</h3>
          <div className="space-y-2">
            {data.expansionSignals.map((s, i) => (
              <div key={i} className="p-3 rounded-lg text-sm" style={{ backgroundColor: "#F0FDF4", borderLeft: "3px solid #7CBF94" }}>
                <p className="italic text-gray-700">« {s.citation} »</p>
                <p className="text-xs mt-1" style={{ color: "#1E7A3C" }}>{s.interpretation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.churnSignals && data.churnSignals.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.churnSignals")}</h3>
          <div className="space-y-2">
            {data.churnSignals.map((s, i) => {
              const sevCfg = { high: "#C0423F", medium: "#926B00", low: "#6B7280" }[s.severity];
              return (
                <div key={i} className="p-3 rounded-lg text-sm" style={{ backgroundColor: "#FFF5F5", borderLeft: `3px solid ${sevCfg}` }}>
                  <p className="italic text-gray-700">« {s.citation} »</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs" style={{ color: sevCfg }}>{s.interpretation}</p>
                    <span className="text-xs text-gray-500">
                      {t("salesCoach.contextSpecific.bounced")}{" "}
                      {s.handled ? t("salesCoach.contextSpecific.yes") : t("salesCoach.contextSpecific.bouncedNo")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.ambassadorActivation && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.ambassadorActivation")}</h3>
          <div className="space-y-1 text-sm">
            <BoolLine label={t("salesCoach.contextSpecific.referenceRequested")} value={data.ambassadorActivation.referenceRequested} />
            <BoolLine label={t("salesCoach.contextSpecific.testimonialAgreed")} value={data.ambassadorActivation.testimonialAgreed} />
            <BoolLine label={t("salesCoach.contextSpecific.crossSellWorkshopProposed")} value={data.ambassadorActivation.crossSellWorkshopProposed} />
          </div>
        </div>
      )}
    </Frame>
  );
}

function BoolLine({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {value ? <CheckCircle2 className="h-4 w-4" style={{ color: "#1E7A3C" }} /> : <XCircle className="h-4 w-4" style={{ color: "#C0423F" }} />}
      <span style={{ color: "#384872" }}>{label}</span>
    </div>
  );
}

// ============================================================
// 6. QUALIFICATION (MEDDIC)
// ============================================================
function QualificationSection({ data }: { data: Extract<ContextSpecificPart, { type: "qualification" }> }) {
  const { t } = useTranslation();
  return (
    <Frame title={t("salesCoach.contextSpecific.frameQualification")} icon={<TargetIcon className="h-5 w-5" />} iconColor="#5A4BBE">
      {data.meddicTable && data.meddicTable.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.meddicCoverage")}</h3>
          <div className="space-y-2">
            {data.meddicTable.map((row, i) => (
              <div key={i}>
                <CoverageBadge status={row.status} label={t(`salesCoach.contextSpecific.meddic.${row.dimension}`, { defaultValue: row.dimension })} />
                {row.evidence && <p className="text-xs italic text-gray-600 mt-1 ml-2">« {row.evidence} »</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.championAnalysis && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.championAnalysis")}</h3>
          <div className="p-3 rounded-lg bg-gray-50 text-sm space-y-1">
            <BoolLine
              label={data.championAnalysis.name
                ? t("salesCoach.contextSpecific.championIdentifiedNamed", { name: data.championAnalysis.name })
                : t("salesCoach.contextSpecific.championIdentified")}
              value={data.championAnalysis.identified}
            />
            <BoolLine label={t("salesCoach.contextSpecific.championActivated")} value={data.championAnalysis.activated} />
            {data.championAnalysis.gap && (
              <p className="text-xs text-gray-600 mt-2">
                <strong>{t("salesCoach.contextSpecific.gap")}</strong> {data.championAnalysis.gap}
              </p>
            )}
          </div>
        </div>
      )}

      {data.decisionProcessTraced && (data.decisionProcessTraced.milestones.length > 0 || data.decisionProcessTraced.dates.length > 0 || data.decisionProcessTraced.people.length > 0) && (
        <div>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.decisionProcessTraced")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <ListBlock title={t("salesCoach.contextSpecific.milestones")} items={data.decisionProcessTraced.milestones} />
            <ListBlock title={t("salesCoach.contextSpecific.dates")} items={data.decisionProcessTraced.dates} />
            <ListBlock title={t("salesCoach.contextSpecific.people")} items={data.decisionProcessTraced.people} />
          </div>
        </div>
      )}
    </Frame>
  );
}

function ListBlock({ title, items }: { title: string; items: string[] }) {
  const { t } = useTranslation();
  return (
    <div className="p-3 rounded-lg bg-gray-50">
      <div className="text-xs uppercase font-semibold mb-1 text-gray-500">{title}</div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 italic">{t("salesCoach.contextSpecific.emptyList")}</p>
      ) : (
        <ul className="text-xs space-y-0.5" style={{ color: "#384872" }}>
          {items.map((it, i) => <li key={i}>• {it}</li>)}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// 7. GENERIC (catch-all)
// ============================================================
function GenericSection({ data }: { data: Extract<ContextSpecificPart, { type: "generic" }> }) {
  const { t } = useTranslation();
  if (!data.suggestion) return null;
  return (
    <Card className="p-4" style={{ backgroundColor: "#F0F7FF", borderLeft: "3px solid #A6D3F9" }}>
      <div className="flex items-start gap-3">
        <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#384872" }} />
        <div>
          <h3 className="text-sm font-semibold mb-1" style={{ color: "#384872" }}>{t("salesCoach.contextSpecific.suggestion")}</h3>
          <p className="text-sm" style={{ color: "#384872" }}>{data.suggestion}</p>
        </div>
      </div>
    </Card>
  );
}
